/** Tracks the current PowerPoint selection for Get Insights. Subscribes to
 *  `DocumentSelectionChanged` (debounced) plus a polling fallback, since that
 *  event unreliably fires for pure shape selections (no text-edit focus) in
 *  PowerPoint desktop. */
import { useCallback, useEffect, useRef, useState } from "react";
import { logError, scrubMessage } from "../../../../../telemetry";
import type { InsightsSelectionContext } from "../../../types";
import { detectInsightsSelection } from "../services/selectionDetector";

const SELECTION_DEBOUNCE_MS = 300;
const SELECTION_POLL_MS = 1000;

export interface UseInsightsSelectionReturn {
  selection: InsightsSelectionContext | null;
}

function logInsightsSelectionError(reason: string, type: string, e: unknown): void {
  logError(
    "error.connector.failure",
    { type, scrubbedMessage: scrubMessage(e instanceof Error ? e.message : String(e)) },
    { connectorSystemName: "deckorate", reason },
  );
}

// Identifies "the same shape" so the poller (which re-detects on a fixed
// timer regardless of whether anything changed) doesn't trigger a re-render
// — and downstream re-analysis — on every tick when the selection is stable.
function selectionKey(ctx: InsightsSelectionContext | null): string | null {
  return ctx ? `${ctx.slideIndex}:${ctx.shapeId}` : null;
}

export function useInsightsSelection(): UseInsightsSelectionReturn {
  const [selection, setSelection] = useState<InsightsSelectionContext | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Discards a detection result if a newer selection change started before
  // it resolved, so a slow lookup can never clobber a more recent one.
  const requestIdRef = useRef(0);
  const lastKeyRef = useRef<string | null>(null);

  const detect = useCallback(() => {
    const requestId = ++requestIdRef.current;
    detectInsightsSelection()
      .then((ctx) => {
        if (requestId !== requestIdRef.current) return;
        const key = selectionKey(ctx);
        if (key === lastKeyRef.current) return;
        lastKeyRef.current = key;
        setSelection(ctx);
      })
      .catch((e: unknown) => {
        logInsightsSelectionError("insights-selection-detect", "InsightsSelectionDetectError", e);
        if (requestId !== requestIdRef.current || lastKeyRef.current === null) return;
        lastKeyRef.current = null;
        setSelection(null);
      });
  }, []);

  useEffect(() => {
    const debouncedDetect = (): void => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(detect, SELECTION_DEBOUNCE_MS);
    };

    detect(); // initial read on mount, ahead of any selection-changed event

    try {
      Office.context.document.addHandlerAsync(
        Office.EventType.DocumentSelectionChanged,
        debouncedDetect,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            logInsightsSelectionError(
              "insights-selection-handler",
              "InsightsSelectionHandlerError",
              result.error?.message ?? "unknown",
            );
          }
        },
      );
    } catch (e) {
      logInsightsSelectionError("insights-selection-handler", "InsightsSelectionHandlerError", e);
    }

    // Polling fallback: catches shape-only selection changes that
    // DocumentSelectionChanged misses (see module doc comment above).
    const pollTimer = setInterval(detect, SELECTION_POLL_MS);

    return () => {
      clearInterval(pollTimer);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      try {
        Office.context.document.removeHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
          { handler: debouncedDetect } as Office.RemoveHandlerOptions,
        );
      } catch (e) {
        // Host doesn't support removeHandlerAsync — nothing more to clean up.
        logInsightsSelectionError("insights-selection-cleanup", "InsightsSelectionCleanupError", e);
      }
    };
  }, [detect]);

  return { selection };
}
